import { Routes } from '@angular/router';
import { provideStates } from '@ngxs/store';
import { MsalGuard } from '@azure/msal-angular';
import { USER_ROLES } from './shared/enums/UserRoles.enum';
import { RolePermissionGuard } from './shared/guards/role-permission.guard';
import { CountryGuard } from './shared/guards/country.guard';
import { ContractTypeGuard } from './shared/guards/contract-type.guard';
import { ServiceLanePoolAccessGuard } from './shared/guards/service-lane-pool-access.guard';
import { TvOrgChartGuard } from './shared/guards/tv-org-chart.guard';
import { HowToState } from './shared/states/howTo/howTo.state';
import { OutOfOfficeState } from './shared/states/out-of-office/out-of-office.state';
import { CONTRACT_TYPES } from './shared/enums/ContractTypes.enum';
import { EmployeeLifecycleMetadataState } from './shared/states/employee-lifecycle-metadata/employee-lifecycle-metadata.state';
import { ElmServiceTaskState } from './shared/states/elm-service-task/elm-service-task.state';
import { environment } from '../environments/environment';

export const APP_ROUTES: Routes = [
  {
    path: 'home',
    loadComponent: () => import('./home/home.component').then(m => m.HomeComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'demo',
    loadChildren: () => import('./demo/demo.routes').then(m => m.DEMO_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [USER_ROLES.TECHNISCHER_CONTROLLER],
    },
  },
  {
    path: 'how-to',
    loadComponent: () => import('./howto/howto.component').then(m => m.HowtoComponent),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [
        USER_ROLES.TECHNISCHER_CONTROLLER,
        USER_ROLES.BUDGETVERANTWORTLICHER,
        USER_ROLES.GRUPPENLEITER,
        USER_ROLES.TRAVEL_ACCOUNTANT,
        USER_ROLES.KAUFMANNISCHER_CONTROLLER,
        USER_ROLES.TEAMMITGLIED,
      ],
    },
    providers: [provideStates([HowToState])],
  },
  {
    path: 'timesheets',
    loadChildren: () => import('./timesheets/timesheets.routes').then(m => m.TIMESHEETS_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [
        USER_ROLES.TECHNISCHER_CONTROLLER,
        USER_ROLES.BUDGETVERANTWORTLICHER,
        USER_ROLES.GRUPPENLEITER,
        USER_ROLES.TRAVEL_ACCOUNTANT,
        USER_ROLES.KAUFMANNISCHER_CONTROLLER,
        USER_ROLES.TEAMMITGLIED,
      ],
    },
  },
  {
    path: 'controlling',
    loadChildren: () => import('./controlling/controlling.routes').then(m => m.CONTROLLING_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [USER_ROLES.TECHNISCHER_CONTROLLER, USER_ROLES.BUDGETVERANTWORTLICHER, USER_ROLES.GRUPPENLEITER],
    },
  },
  {
    path: 'personal-information',
    loadComponent: () => import('./personal-information/personal-information.component').then(m => m.PersonalInformationComponent),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [
        USER_ROLES.TECHNISCHER_CONTROLLER,
        USER_ROLES.BUDGETVERANTWORTLICHER,
        USER_ROLES.GRUPPENLEITER,
        USER_ROLES.TRAVEL_ACCOUNTANT,
        USER_ROLES.KAUFMANNISCHER_CONTROLLER,
        USER_ROLES.TEAMMITGLIED,
      ],
    },
  },
  {
    path: 'release-notes',
    loadComponent: () => import('./release-notes/release-notes.component').then(m => m.ReleaseNotesComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'projects',
    loadChildren: () => import('./projects/projects.routes').then(m => m.PROJECTS_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [
        USER_ROLES.TECHNISCHER_CONTROLLER,
        USER_ROLES.BUDGETVERANTWORTLICHER,
        USER_ROLES.GRUPPENLEITER,
        USER_ROLES.TRAVEL_ACCOUNTANT,
        USER_ROLES.KAUFMANNISCHER_CONTROLLER,
        USER_ROLES.TEAMMITGLIED,
      ],
    },
  },
  {
    path: 'travels',
    loadChildren: () => import('./travels/travels.routes').then(m => m.TRAVELS_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [
        USER_ROLES.TECHNISCHER_CONTROLLER,
        USER_ROLES.BUDGETVERANTWORTLICHER,
        USER_ROLES.GRUPPENLEITER,
        USER_ROLES.TRAVEL_ACCOUNTANT,
        USER_ROLES.KAUFMANNISCHER_CONTROLLER,
        USER_ROLES.TEAMMITGLIED,
      ],
    },
  },
  {
    path: 'employees',
    loadChildren: () => import('./employees/employees.routes').then(m => m.EMPLOYEES_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [USER_ROLES.KAUFMANNISCHER_CONTROLLER, USER_ROLES.TECHNISCHER_CONTROLLER, USER_ROLES.GRUPPENLEITER],
    },
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.routes').then(m => m.ADMIN_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [USER_ROLES.TECHNISCHER_CONTROLLER],
    },
  },
  {
    path: 'approvals',
    loadChildren: () => import('./approvals/approvals.routes').then(m => m.APPROVALS_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard, TvOrgChartGuard],
    data: {
      roles: [USER_ROLES.GRUPPENLEITER, USER_ROLES.TECHNISCHER_CONTROLLER, USER_ROLES.BUDGETVERANTWORTLICHER],
    },
  },
  ...(environment.features.employeeLifecycleManagement
    ? [
        {
          path: 'service-line-tasks',
          loadComponent: () => import('./service-line-tasks/service-line-tasks.component').then(m => m.ServiceLineTasksComponent),
          canActivate: [MsalGuard, ServiceLanePoolAccessGuard],
          providers: [provideStates([EmployeeLifecycleMetadataState, ElmServiceTaskState])],
        },
      ]
    : []),
  {
    path: 'accounts',
    loadChildren: () => import('./accounts/accounts.routes').then(m => m.ACCOUNTS_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [
        USER_ROLES.KAUFMANNISCHER_CONTROLLER,
        USER_ROLES.TECHNISCHER_CONTROLLER,
        USER_ROLES.BUDGETVERANTWORTLICHER,
        USER_ROLES.GRUPPENLEITER,
      ],
    },
  },
  {
    path: 'accounting',
    loadChildren: () => import('./accounting/accounting.routes').then(m => m.ACCOUNTING_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [USER_ROLES.TECHNISCHER_CONTROLLER],
    },
  },
  {
    path: 'externals',
    loadChildren: () => import('./externals/externals.routes').then(m => m.EXTERNALS_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [USER_ROLES.TECHNISCHER_CONTROLLER],
    },
  },
  {
    path: 'other-costs',
    loadChildren: () => import('./other-costs/other-costs.routes').then(m => m.OTHER_COSTS_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard],
    data: {
      roles: [
        USER_ROLES.TECHNISCHER_CONTROLLER,
        USER_ROLES.BUDGETVERANTWORTLICHER,
        USER_ROLES.GRUPPENLEITER,
        USER_ROLES.TEAMMITGLIED,
        USER_ROLES.ONLINE_SUPPORT,
      ],
    },
  },
  {
    path: 'out-of-office',
    loadChildren: () => import('./out-of-office/out-of-office.routes').then(m => m.OUT_OF_OFFICE_ROUTES),
    canActivate: [MsalGuard, RolePermissionGuard, CountryGuard, ContractTypeGuard],
    providers: [provideStates([OutOfOfficeState])],
    data: {
      roles: [
        USER_ROLES.TECHNISCHER_CONTROLLER,
        USER_ROLES.BUDGETVERANTWORTLICHER,
        USER_ROLES.GRUPPENLEITER,
        USER_ROLES.TRAVEL_ACCOUNTANT,
        USER_ROLES.KAUFMANNISCHER_CONTROLLER,
        USER_ROLES.TEAMMITGLIED,
      ],
      countries: ['Germany'],
      contractTypes: [CONTRACT_TYPES.Festangestellter, CONTRACT_TYPES.Student],
    },
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.routes').then(m => m.AUTH_ROUTES),
  },
  {
    path: 'login',
    loadComponent: () => import('./shared/components/login/login-page.component').then(m => m.LoginPageComponent),
    canActivate: [MsalGuard, RolePermissionGuard],
  },
  {
    path: 'maintenance',
    loadComponent: () => import('./shared/components/maintenace-page/maintenance-page.component').then(m => m.MaintenancePageComponent),
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
